import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X } from "lucide-react";

const empty = { slug: "", name: "", subtitle: "", description: "", features: [], image_url: "", accent: "red", order: 0, active: true };

export default function AdminSimulators() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    const { data } = await api.get("/admin/simulators");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    const payload = { ...editing, features: (editing.features_str || editing.features?.join("\n") || "").split("\n").map((s) => s.trim()).filter(Boolean) };
    delete payload.features_str;
    try {
      if (editing.id) await api.put(`/admin/simulators/${editing.id}`, payload);
      else await api.post("/admin/simulators", payload);
      toast.success("Salvo");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao salvar");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Excluir simulador?")) return;
    await api.delete(`/admin/simulators/${id}`);
    load();
  };

  return (
    <div data-testid="admin-simulators">
      <Header title="Simuladores" onCreate={() => setEditing({ ...empty })} />
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((s) => (
          <div key={s.id} className="rounded-xl border border-white/5 bg-[#0a0a0a] overflow-hidden">
            {s.image_url && <img src={s.image_url} alt={s.name} className="w-full h-40 object-cover" />}
            <div className="p-5">
              <div className="flex justify-between items-start">
                <div>
                  <div className="overline text-neutral-500">{s.subtitle}</div>
                  <div className="font-display font-bold text-lg">{s.name}</div>
                </div>
                <span className={`text-[10px] px-2 py-1 rounded-full uppercase ${s.active ? "bg-green-500/15 text-green-400" : "bg-neutral-500/15 text-neutral-500"}`}>
                  {s.active ? "ATIVO" : "INATIVO"}
                </span>
              </div>
              <div className="mt-4 flex gap-2">
                <button onClick={() => setEditing({ ...s, features_str: s.features?.join("\n") })} className="flex-1 rounded-lg border border-white/10 hover:border-[#008CFF] py-2 text-xs uppercase tracking-widest" data-testid={`edit-sim-${s.slug}`}>
                  <Pencil className="w-3 h-3 inline mr-1" /> Editar
                </button>
                <button onClick={() => remove(s.id)} className="rounded-lg border border-white/10 hover:border-[#E31B23] px-3 py-2 text-xs" data-testid={`del-sim-${s.slug}`}>
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <EditModal onClose={() => setEditing(null)} onSave={save} title={editing.id ? "Editar simulador" : "Novo simulador"}>
          <Grid2>
            <Input label="Slug*" v={editing.slug} on={(v) => setEditing({ ...editing, slug: v })} />
            <Input label="Nome*" v={editing.name} on={(v) => setEditing({ ...editing, name: v })} />
            <Input label="Subtítulo" v={editing.subtitle} on={(v) => setEditing({ ...editing, subtitle: v })} />
            <Input label="Ordem" type="number" v={editing.order} on={(v) => setEditing({ ...editing, order: Number(v) })} />
          </Grid2>
          <Input label="URL da imagem" v={editing.image_url} on={(v) => setEditing({ ...editing, image_url: v })} />
          <TextArea label="Descrição" v={editing.description} on={(v) => setEditing({ ...editing, description: v })} rows={3} />
          <TextArea label="Características (uma por linha)" v={editing.features_str ?? editing.features?.join("\n") ?? ""} on={(v) => setEditing({ ...editing, features_str: v })} rows={6} />
          <Grid2>
            <div>
              <label className="block overline text-neutral-400 mb-2">Cor de destaque</label>
              <select value={editing.accent} onChange={(e) => setEditing({ ...editing, accent: e.target.value })} className="w-full bg-[#111] border border-white/10 rounded-lg px-4 py-3">
                <option value="red">Vermelho</option>
                <option value="blue">Azul</option>
              </select>
            </div>
            <label className="flex items-center gap-2 mt-8">
              <input type="checkbox" checked={editing.active} onChange={(e) => setEditing({ ...editing, active: e.target.checked })} />
              <span className="text-sm">Ativo</span>
            </label>
          </Grid2>
        </EditModal>
      )}
    </div>
  );
}

export function Header({ title, onCreate }) {
  return (
    <div className="mb-8 flex items-end justify-between">
      <div>
        <div className="overline text-neutral-500 mb-2">Gerenciar</div>
        <h1 className="font-display font-black text-3xl">{title}</h1>
      </div>
      <button onClick={onCreate} className="inline-flex items-center gap-2 rounded-full bg-[#E31B23] px-5 py-2.5 text-sm font-semibold glow-red" data-testid="admin-new-btn">
        <Plus className="w-4 h-4" /> Novo
      </button>
    </div>
  );
}

export function EditModal({ children, onClose, onSave, title }) {
  return (
    <div className="fixed inset-0 z-[600] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/85 backdrop-blur" onClick={onClose} />
      <div className="relative w-full max-w-2xl rounded-2xl border border-white/10 bg-[#0a0a0a] p-8 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <div className="font-display font-bold text-xl">{title}</div>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-4">{children}</div>
        <div className="mt-8 flex gap-3 justify-end">
          <button onClick={onClose} className="rounded-full border border-white/15 px-5 py-2.5 text-sm">Cancelar</button>
          <button onClick={onSave} className="rounded-full bg-[#E31B23] px-6 py-2.5 text-sm font-semibold glow-red" data-testid="admin-save-btn">Salvar</button>
        </div>
      </div>
    </div>
  );
}

export const Grid2 = ({ children }) => <div className="grid md:grid-cols-2 gap-4">{children}</div>;

export const Input = ({ label, v, on, type = "text" }) => (
  <div>
    <label className="block overline text-neutral-400 mb-2">{label}</label>
    <input type={type} value={v ?? ""} onChange={(e) => on(e.target.value)} className="w-full bg-[#111] border border-white/10 rounded-lg px-4 py-3 focus:border-[#008CFF] outline-none" />
  </div>
);

export const TextArea = ({ label, v, on, rows = 3 }) => (
  <div>
    <label className="block overline text-neutral-400 mb-2">{label}</label>
    <textarea rows={rows} value={v ?? ""} onChange={(e) => on(e.target.value)} className="w-full bg-[#111] border border-white/10 rounded-lg px-4 py-3 focus:border-[#008CFF] outline-none resize-none" />
  </div>
);
